import type { ConversationListItem, MessageItem } from '@/shared/api';

export const INBOX_FILTERS = ['Alle', 'BookingCom', 'Airbnb', 'Sonstiges'] as const;
export type InboxFilter = (typeof INBOX_FILTERS)[number];

type ReplyState = Pick<ConversationListItem, 'replyOpen' | 'isClosed'>;

/** Dieselbe Regel wie der Server: offen und nicht geschlossen. */
export function awaitingReply(conversation: ReplyState): boolean {
  return conversation.replyOpen && !conversation.isClosed;
}

export function channelLabel(channel: string | null | undefined): string {
  if (channel === 'BookingCom') return 'Booking.com';
  if (channel === 'Airbnb') return 'Airbnb';
  return channel && channel !== 'Unknown' ? channel : 'Sonstiges';
}

export function filterLabel(filter: InboxFilter): string {
  if (filter === 'Alle') return 'Alle Kanäle';
  return channelLabel(filter);
}

export function classificationLabel(
  classification: ConversationListItem['classification'],
): string | null {
  if (classification === 'Inquiry') return 'Anfrage';
  if (classification === 'Unresolved') return 'Ungeklärt';
  return null;
}

/** „wartet seit 30 Min.“ — nur aus der Zeit der letzten Gastnachricht, die der Server liefert. */
export function waitingSince(receivedAt: string | null, now: Date): string | null {
  if (!receivedAt) return null;
  const minutes = Math.floor((now.getTime() - new Date(receivedAt).getTime()) / 60_000);
  if (Number.isNaN(minutes)) return null;
  if (minutes < 1) return 'wartet seit gerade eben';
  if (minutes < 60) return `wartet seit ${minutes} Min.`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `wartet seit ${hours} Std.`;
  const days = Math.floor(hours / 24);
  return days === 1 ? 'wartet seit 1 Tag' : `wartet seit ${days} Tagen`;
}

/**
 * Eine Gastgebernachricht ohne Verfasser kam über den Kanal herein — geschrieben direkt in Airbnb
 * oder Booking.com, nicht in StayOn. Mensch oder geplante Nachricht des Kanals, das sagt der Kanal
 * nicht.
 */
export function cameFromChannel(message: MessageItem): boolean {
  return message.sender === 'Host' && message.composedBy === null && message.sendStatus === 'Sent';
}

function localDay(iso: string): string {
  return new Date(iso).toLocaleDateString('sv-SE');
}

/** Tagestrenner im Verlauf: „Heute“, „Gestern“ oder das Datum. */
export function dayLabel(iso: string, now: Date): string {
  const day = localDay(iso);
  if (day === now.toLocaleDateString('sv-SE')) return 'Heute';
  const yesterday = new Date(now);
  yesterday.setDate(now.getDate() - 1);
  if (day === yesterday.toLocaleDateString('sv-SE')) return 'Gestern';
  return new Date(iso).toLocaleDateString('de-DE', {
    weekday: 'short',
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  });
}

const SEND_ERRORS: Record<string, string> = {
  send_expired: 'zu lange nicht zugestellt, abgebrochen',
  evidence_inconclusive: 'geprüft, Ergebnis unklar',
};

/** Grund eines gescheiterten Versands als Satzteil; unbekannte Codes bleiben sichtbar. */
export function sendErrorText(code: string): string {
  return SEND_ERRORS[code] ?? `Grund: ${code}`;
}

export function messageTime(message: MessageItem): string {
  return message.sentAt ?? message.receivedAt ?? message.occurredAt;
}
